import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { transferAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import './TransferList.css';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: '', label: 'All statuses' },
  { value: 'Opened', label: 'Opened' },
  { value: 'In Review', label: 'In Review' },
  { value: 'Ready to Send', label: 'Ready to Send' },
  { value: 'File Sent', label: 'File Sent' },
  { value: 'Confirmed Receipt', label: 'Confirmed Receipt' },
  { value: 'Voided', label: 'Voided' },
];

function TransferList() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [receivingCfcFilter, setReceivingCfcFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // default: Opened, In Review, Ready to Send, File Sent (exclude Confirmed Receipt & Voided)
  const [hintExpanded, setHintExpanded] = useState(false);

  useEffect(() => {
    if (!hasPermission('transfers.view')) {
      navigate(tenantPath('/dashboard'), { replace: true });
      return;
    }
    loadTransfers();
  }, [navigate, tenantPath, statusFilter]);

  const receivingCfcOptions = useMemo(() => {
    const set = new Set();
    transfers.forEach((t) => { if (t.receiving_cfc != null && t.receiving_cfc !== '') set.add(t.receiving_cfc); });
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [transfers]);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      // 'active' = don't send status (get all), filter client-side to exclude Confirmed Receipt & Voided
      if (statusFilter && statusFilter !== 'active') params.status = statusFilter;
      params.exclude_voided = 'true';
      const res = await transferAPI.list(params);
      setTransfers(res.data?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load transfers');
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const clientName = (t) =>
    [t.client_last_name, t.client_first_name].filter(Boolean).join(', ') || '—';

  const displayedTransfers = useMemo(() => {
    let out = transfers;
    if (clientSearch.trim()) {
      const q = clientSearch.trim().toLowerCase();
      out = out.filter((t) => {
        const name = clientName(t).toLowerCase();
        const ei = (t.client_ei_number || '').toString().toLowerCase();
        return name.includes(q) || ei.includes(q);
      });
    }
    if (receivingCfcFilter !== '') {
      const cfc = receivingCfcFilter === 'null' ? null : Number(receivingCfcFilter);
      out = out.filter((t) => (t.receiving_cfc == null && cfc == null) || (t.receiving_cfc != null && Number(t.receiving_cfc) === cfc));
    }
    if (statusFilter === 'active') {
      out = out.filter((t) => {
        const s = (t.status || '').trim();
        return s !== 'Confirmed Receipt' && s.toLowerCase() !== 'voided';
      });
    } else if (statusFilter) {
      out = out.filter((t) => (t.status || '') === statusFilter);
    } else {
      out = out.filter((t) => (t.status || '').toLowerCase() !== 'voided');
    }
    return out;
  }, [transfers, clientSearch, receivingCfcFilter, statusFilter]);

  return (
    <div className="transfer-list-page">
      <div className="transfer-list-header">
        <div className="transfer-list-header-row">
          <button
            type="button"
            className="transfer-list-title-with-hint"
            onClick={() => setHintExpanded((prev) => !prev)}
            aria-expanded={hintExpanded}
            aria-label={hintExpanded ? 'Hide description' : 'Show description'}
          >
            Transfers
            <span className="transfer-list-hint-icon" aria-hidden>ⓘ</span>
          </button>
          {hasPermission('transfers.create') && (
            <Link to={tenantPath('/lists/transfers/new')} className="btn-new">
              + New transfer
            </Link>
          )}
        </div>
        {hintExpanded && (
          <p className="transfer-list-hint-text">Outbound client transfers to other CFCs. Track status and referral tracking dates. Use the filters below the column headings to narrow by client, receiving CFC, or status.</p>
        )}
      </div>

      {error && <p className="transfer-list-error">{error}</p>}

      <div className="transfer-list-count">
        {loading && transfers.length === 0
          ? 'Loading…'
          : displayedTransfers.length === 0
            ? '0 Transfers'
            : displayedTransfers.length === 1
              ? '1 Transfer'
              : `${displayedTransfers.length} Transfers`}
      </div>
      <div className="transfer-list-table-wrap">
        <table className="transfer-list-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Receiving CFC</th>
              <th>Status</th>
            </tr>
            <tr className="transfer-list-filter-row">
              <th>
                <input
                  type="text"
                  className="transfer-list-filter-input"
                  placeholder="Search client..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  aria-label="Filter by client name"
                />
              </th>
              <th>
                <select
                  className="transfer-list-filter-select"
                  value={receivingCfcFilter}
                  onChange={(e) => setReceivingCfcFilter(e.target.value)}
                  aria-label="Filter by receiving CFC"
                >
                  <option value="">All</option>
                  {receivingCfcOptions.map((n) => (
                    <option key={n} value={n}>CFC {n}</option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  className="transfer-list-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && transfers.length === 0 ? (
              <tr>
                <td colSpan="3" className="no-data">Loading...</td>
              </tr>
            ) : displayedTransfers.length === 0 ? (
              <tr>
                <td colSpan="3" className="no-data">
                  {transfers.length === 0 ? 'No transfers found. Create one with + New transfer.' : 'No transfers match your search.'}
                </td>
              </tr>
            ) : (
              displayedTransfers.map((t) => (
                <tr
                  key={t.id}
                  className="clickable-row"
                  onClick={() => navigate(tenantPath(`/lists/transfers/${t.id}`))}
                >
                  <td>{clientName(t)}</td>
                  <td>{t.receiving_cfc != null ? `CFC ${t.receiving_cfc}` : '—'}</td>
                  <td>
                    {t.status || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TransferList;
